import { useEffect, useRef, useState } from 'react';
import {
  usePatchShippingOrderMutation,
  useDeleteShippingOrderMutation,
  useGetOrderCompanyContactsQuery,
} from 'app/services/projectBoardApi';
import type { IBoardUser, ICompany, IShippingOrder } from 'models';
import {
  PHONE_PATTERN,
  useOrderCreatorName,
  scrollMainContainerToCard,
  ClearableInput,
  OrderTextarea,
  OrderDateInput,
  OrderTimeInput,
  DeleteOrderPopup,
} from '../orders/orderShared';

/**
 * ShippingOrderView (`li.col-xs-12`, shipping_order.tmpl ~40KB) — inventory
 * §B6 / parity 183–192. Faithful markup port: bootstrap-accordion card with
 * the hidden collapse-checkbox + icon-add label, the `.collapsed-mode`
 * summary (incl. the template's DUPLICATE `#collapsed_equipment_for_delivery`
 * id on the Actual Shipping cell — kept as markup parity; our summary cells
 * derive from the model, so the legacy jQuery dup-id mirror glitch — a
 * textarea blur transiently overwriting the Actual Shipping cell — does not
 * reproduce), and the full expanded form with exact names / maxlengths /
 * placeholders / tabindexes (incl. the template's `id="datepicker"` on
 * sched_loading_date and the off-load complete-time `.empty` class keying
 * off the START time — template quirks kept).
 *
 * Saves: per-field PATCH of ONLY the changed field(s) (model autosave bound
 * from construction); Freight Co. change sends {freight_co_id,
 * freight_co_contact_id: null} in one PATCH and repopulates the contact
 * select from the per-company session cache (RTK per-arg cache).
 *
 * Collapse: expanded ⇔ the collapse-checkbox; toggling animates
 * `.main-container` scrollTop to the card (scrollUpCard — the legacy scrolls
 * on every checkbox change, both directions).
 *
 * Read-only (no shipping access): inputs readonly, textareas + buttons
 * non-clickable, collapse stays ENABLED (parity 191). Selects and clear
 * icons are also locked here (working read-only — 3b precedent; the legacy
 * per-card gating misses both), and new cards are gated like existing ones
 * (parity 191 "fix in React + verify note").
 *
 * [NEG] honored: dead `change input[type=time]` binding (no such inputs);
 * `validateDateTime`'s '-'→'.' rewrite targets only the commented-out
 * `.date-placeholder` spans — a DOM no-op, not ported.
 */
const ShippingOrderCard = ({
  projectId,
  order,
  companies,
  me,
  access,
  initiallyExpanded,
  autoFocusTitle,
}: {
  projectId: number;
  order: IShippingOrder;
  companies: ICompany[];
  me: IBoardUser;
  access: boolean;
  initiallyExpanded: boolean;
  autoFocusTitle: boolean;
}) => {
  const [patchOrder] = usePatchShippingOrderMutation();
  const [deleteOrder] = useDeleteShippingOrderMutation();
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [showDelete, setShowDelete] = useState(false);
  const liRef = useRef<HTMLLIElement | null>(null);
  const newCardBootRef = useRef(false);

  // New-card boot (`checkboxState: true` + init()'s order_title focus,
  // parity 183). Effect-based because the created row can land in the cache
  // (and mount this card) a tick before the tab marks it as new.
  useEffect(() => {
    if (autoFocusTitle && !newCardBootRef.current) {
      newCardBootRef.current = true;
      setExpanded(true);
      liRef.current
        ?.querySelector<HTMLInputElement>('input[name="order_title"]')
        ?.focus();
    }
  }, [autoFocusTitle]);

  const locked = !access;
  const creatorName = useOrderCreatorName(order.order_created_by, me);

  const coId = order.freight_co_id;
  // 'CompanyContactCollection'/{co_id} session cache (orderShared / api note).
  const { data: contacts } = useGetOrderCompanyContactsQuery(Number(coId), {
    skip: coId == null,
  });

  const companyName = companies.find((c) => c.id === Number(coId))?.name ?? '';
  const contactName =
    order.freight_co_contact_id != null
      ? contacts?.find((c) => c.id === Number(order.freight_co_contact_id))?.name ?? ''
      : '';

  const commit = (name: string, value: unknown) => {
    patchOrder({
      projectId,
      id: order.id,
      set: { [name]: value },
      body: { [name]: value },
    });
  };

  // changeFreightCoSelect: int-or-null + contact reset in ONE set/PATCH.
  const commitCompany = (raw: string) => {
    let v: number | null = parseInt(raw, 10);
    if (!v) v = null;
    const set = { freight_co_id: v, freight_co_contact_id: null };
    patchOrder({ projectId, id: order.id, set, body: set });
  };

  const commitContact = (raw: string) => {
    let v: number | null = parseInt(raw, 10);
    if (!v) v = null;
    commit('freight_co_contact_id', v);
  };

  const toggleCollapse = () => {
    setExpanded((v) => !v);
    // scrollUpCard fires on every collapse-checkbox change (both directions).
    scrollMainContainerToCard(liRef.current);
  };

  const v = (key: keyof IShippingOrder): string =>
    (order[key] as string | null | undefined) ?? '';

  const addrField = (key: keyof IShippingOrder) => (
    <div className={`left${order[key] ? ' not-empty ' : ''}`} id={`collapsed_${String(key)}`}>
      {v(key)}
    </div>
  );

  return (
    <li className="col-xs-12" ref={liRef}>
      <div className="panel-group col-xs-12" id={`accordion${order.id}`}>
        <div className="panel panel-default row">
          <div
            className={`panel-heading col-xs-12${expanded ? '' : ' collapsed'}`}
            data-toggle="collapse"
            data-parent={`#accordion${order.id}`}
            onClick={(e) => {
              // The label fires a SYNTHETIC checkbox click; both it and the
              // original click bubble here. Toggling here too would double-fire
              // (net no-op — 3e verify finding 2). Label/checkbox clicks are
              // handled by the checkbox onChange; heading handles the rest.
              const t = e.target as HTMLElement;
              if (t.closest('label.collapse-btn') || t.closest('input.collapse-checkbox')) return;
              toggleCollapse();
            }}
          >
            <input
              id={`shipping_order_no${order.id}`}
              type="checkbox"
              className="hidden collapse-checkbox"
              tabIndex={-1}
              checked={expanded}
              onChange={toggleCollapse}
            />
            <label
              htmlFor={`shipping_order_no${order.id}`}
              className="icon-add right collapse-btn ka-has-tooltip"
              tooltip="Expand"
              tabIndex={-1}
            />
            <div className="collapsed-mode list-inline">
              <div className="col-xs-12 distance">
                <div className="col-xs-2">
                  <div className="row">
                    <div className="value" id="collapsed_order_title">
                      {v('order_title')}
                    </div>
                  </div>
                </div>
                <div className="col-xs-10">
                  <div className="row">
                    <div className="col-xs-4">
                      <div className="row ">
                        <div className="label-text col-xs-5">Order No.</div>
                        <div className="value col-xs-7" id="collapsed_order_number">
                          {v('order_number')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4 ">
                      <div className="row">
                        <div className="label-text col-xs-5">Scheduled Shipping</div>
                        <div className="value col-xs-7" id="collapsed_sched_ship_date">
                          {v('sched_ship_date')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4 ">
                      <div className="row">
                        <div className="label-text col-xs-5">Actual Shipping</div>
                        {/* Template quirk: dup id (kept for markup parity). */}
                        <div className="value col-xs-7" id="collapsed_equipment_for_delivery">
                          {v('actual_ship_date')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 distance">
                <div className="col-xs-10 col-xs-offset-2">
                  <div className="row">
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Equipment</div>
                        <div className="value col-xs-7" id="collapsed_equipment_for_delivery">
                          {v('equipment_for_delivery')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Scheduled Delivery</div>
                        <div className="value col-xs-7" id="collapsed_sched_delivery_date">
                          {v('sched_delivery_date')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Actual Delivery</div>
                        <div className="value col-xs-7" id="collapsed_actual_delivery_date">
                          {v('actual_delivery_date')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 distance">
                <div className="col-xs-10 col-xs-offset-2">
                  <div className="row">
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Freight Co.</div>
                        <div className="value col-xs-7">
                          <div className="row">
                            <div className="col-xs-12" id="collapsed_freight_co_id">
                              {companyName}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-xs-12" id="collapsed_freight_co_contact_id">
                              {contactName}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-8">
                      <div className="row">
                        <div className="label-text col-xs-3 custom-col-xs-3 ">Address</div>
                        <div className="value col-xs-9">
                          {addrField('delivery_delivery_address_line_1')}
                          {addrField('delivery_delivery_address_line_2')}
                          {addrField('delivery_address_city')}
                          {addrField('delivery_address_st')}
                          {addrField('delivery_address_postal_code')}
                          {addrField('delivery_address_country')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            id={`collapse${order.id}`}
            className={`panel-collapse collapse${expanded ? ' in' : ''} col-xs-12`}
          >
            <div className="row">
              <div className="col-xs-12">
                <div className="row">
                  <div className="col-lg-5 col-md-12">
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Title</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12"
                            name="order_title"
                            value={v('order_title')}
                            maxLength={25}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Order No.</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12"
                            name="order_number"
                            value={v('order_number')}
                            maxLength={15}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Description</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12"
                            name="order_description"
                            value={v('order_description')}
                            maxLength={55}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">By</div>
                        <div className="col-xs-10">
                          <input
                            className="col-xs-12"
                            type="text"
                            name="order_created_by"
                            value={creatorName}
                            readOnly
                            minLength={0}
                            maxLength={35}
                            tabIndex={-1}
                            onChange={() => undefined}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Date created</div>
                        <div className="col-xs-10">
                          <input
                            className="col-xs-12"
                            type="text"
                            name="order_created_date"
                            value={v('order_created_date')}
                            readOnly
                            minLength={0}
                            maxLength={10}
                            tabIndex={-1}
                            onChange={() => undefined}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-7 col-md-12">
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="col-xs-offset-2 col-xs-10 date-table-heading">
                        <div className="row">
                          <div className="col-xs-6">
                            <div className="row">SCHEDULED</div>
                          </div>
                          <div className="col-xs-6">ACTUAL</div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Loading (date + start/complete time triplets) ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-2 label-text">Loading</div>
                        <div className="col-xs-10">
                          <div className="row">
                            <div className="sched-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="sched_loading_date"
                                    value={v('sched_loading_date')}
                                    locked={locked}
                                    id="datepicker"
                                    autoComplete="off"
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    className=" time-input pointer"
                                    name="sched_loading_time_start"
                                    value={v('sched_loading_time_start')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-2 time-to">
                                  <span className="to">to</span>
                                </div>
                                <div className="col-xs-3 padding-left-0">
                                  <OrderTimeInput
                                    name="sched_loading_time_complete"
                                    value={v('sched_loading_time_complete')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="actual-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="actual_loading_date"
                                    value={v('actual_loading_date')}
                                    locked={locked}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    name="actual_loading_time_start"
                                    value={v('actual_loading_time_start')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-2 time-to">
                                  <span className="to">to</span>
                                </div>
                                <div className="col-xs-3 padding-left-0">
                                  <OrderTimeInput
                                    name="actual_loading_time_complete"
                                    value={v('actual_loading_time_complete')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Shipping (date + single time) ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-2 label-text">Shipping</div>
                        <div className="col-xs-10">
                          <div className="row">
                            <div className="sched-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="sched_ship_date"
                                    value={v('sched_ship_date')}
                                    locked={locked}
                                    tabIndex={-1}
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    name="sched_ship_time"
                                    value={v('sched_ship_time')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="actual-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="actual_ship_date"
                                    value={v('actual_ship_date')}
                                    locked={locked}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    name="actual_ship_time"
                                    value={v('actual_ship_time')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Delivery (date + single time) ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-2 label-text">Delivery</div>
                        <div className="col-xs-10">
                          <div className="row">
                            <div className="sched-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="sched_delivery_date"
                                    value={v('sched_delivery_date')}
                                    locked={locked}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    name="sched_delivery_time"
                                    value={v('sched_delivery_time')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="actual-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="actual_delivery_date"
                                    value={v('actual_delivery_date')}
                                    locked={locked}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    name="actual_delivery_time"
                                    value={v('actual_delivery_time')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Off-Loading (date + start/complete time triplets) ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-2 label-text">Off-Loading</div>
                        <div className="col-xs-10">
                          <div className="row">
                            <div className="sched-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="sched_off_load_date"
                                    value={v('sched_off_load_date')}
                                    locked={locked}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    name="sched_off_load_time_start"
                                    value={v('sched_off_load_time_start')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-2 time-to">
                                  <span className="to">to</span>
                                </div>
                                <div className="col-xs-3 padding-left-0">
                                  {/* Template quirk: the complete-time .empty
                                      class keys off the START time. */}
                                  <OrderTimeInput
                                    className="time-input pointer "
                                    name="sched_off_load_time_complete"
                                    value={v('sched_off_load_time_complete')}
                                    emptyFrom={v('sched_off_load_time_start')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="actual-inputs col-xs-6">
                              <div className="row">
                                <div className="col-xs-4 padding-right-0">
                                  <OrderDateInput
                                    name="actual_off_load_date"
                                    value={v('actual_off_load_date')}
                                    locked={locked}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-3 padding-right-0">
                                  <OrderTimeInput
                                    name="actual_off_load_time_start"
                                    value={v('actual_off_load_time_start')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                                <div className="col-xs-2 time-to">
                                  <span className="to">to</span>
                                </div>
                                <div className="col-xs-3 padding-left-0">
                                  <OrderTimeInput
                                    name="actual_off_load_time_complete"
                                    value={v('actual_off_load_time_complete')}
                                    locked={locked}
                                    onCommit={commit}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row" style={{ paddingTop: 49, paddingBottom: 20 }}>
                  <div className="col-lg-5 col-md-12" id="equipment">
                    <div className="col-xs-12 input-vertical-distance textarea-top-padding">
                      <div className="row">
                        <div className="col-xs-2 label-text">Equipment</div>
                        <div className="col-xs-10">
                          <OrderTextarea
                            name="equipment_for_delivery"
                            value={v('equipment_for_delivery')}
                            maxLength={255}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-2 label-text">Freight Co.</div>
                        <div className="col-xs-10 label-company">
                          <div className="row">
                            <div className="col-xs-6">
                              <select
                                className="freight_co_field full-width"
                                name="freight_co_id"
                                required
                                disabled={locked}
                                value={coId != null ? String(coId) : 'None'}
                                onChange={(e) => commitCompany(e.target.value)}
                              >
                                <option value="None">-----</option>
                                {companies.map((c) => (
                                  <option key={c.id} value={String(c.id)}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-xs-6">
                              <select
                                className="xs-input freight_co_contact_field full-width"
                                name="freight_co_contact_id"
                                required
                                disabled={locked}
                                value={
                                  order.freight_co_contact_id != null
                                    ? String(order.freight_co_contact_id)
                                    : 'None'
                                }
                                onChange={(e) => commitContact(e.target.value)}
                              >
                                <option value="None">-----</option>
                                {coId != null &&
                                  (contacts ?? []).map((c) => (
                                    <option key={c.id} value={String(c.id)}>
                                      {c.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Driver</div>
                        <div className="col-xs-10">
                          <div className="row">
                            <div className="col-xs-6">
                              <ClearableInput
                                className="full-width"
                                name="freight_co_driver"
                                value={v('freight_co_driver')}
                                placeholder="Name"
                                maxLength={25}
                                locked={locked}
                                autoComplete={null}
                                onCommit={commit}
                              />
                            </div>
                            <div className="col-xs-6">
                              <ClearableInput
                                className="full-width phone-number-input"
                                name="freight_co_driver_mob"
                                value={v('freight_co_driver_mob')}
                                placeholder="Mobile"
                                maxLength={15}
                                pattern={PHONE_PATTERN}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Email</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12 press_english"
                            type="email"
                            name="freight_co_driver_email"
                            value={v('freight_co_driver_email')}
                            maxLength={35}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Truck Info</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12"
                            name="type_qty_trucks"
                            value={v('type_qty_trucks')}
                            maxLength={65}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance textarea-top-padding">
                      <div className="row">
                        <div className="label-text col-xs-2">Instructions</div>
                        <div className="col-xs-10">
                          <OrderTextarea
                            name="instructions"
                            value={v('instructions')}
                            maxLength={255}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-7 col-md-12">
                    <div className="col-xs-12">
                      <div className="row">
                        <div className="label-text col-xs-2">Deliver to</div>
                        <div className="col-xs-10 input-with-placeholder-group input-vertical-distance">
                          <ClearableInput
                            className="col-xs-12 input-with-placeholder"
                            id="delivery_delivery_address_line_1"
                            name="delivery_delivery_address_line_1"
                            value={v('delivery_delivery_address_line_1')}
                            placeholder="Address Line 1"
                            maxLength={65}
                            locked={locked}
                            onCommit={commit}
                          />
                          <label
                            className="input-placeholder"
                            htmlFor="delivery_delivery_address_line_1"
                          >
                            Address Line 1
                          </label>
                        </div>
                        <div className="col-xs-offset-2 col-xs-10 input-with-placeholder-group input-vertical-distance">
                          <ClearableInput
                            className="col-xs-12 input-with-placeholder"
                            id="delivery_delivery_address_line_2"
                            name="delivery_delivery_address_line_2"
                            value={v('delivery_delivery_address_line_2')}
                            placeholder="Address Line 2"
                            maxLength={65}
                            locked={locked}
                            onCommit={commit}
                          />
                          <label
                            className="input-placeholder"
                            htmlFor="delivery_delivery_address_line_2"
                          >
                            Address Line 2
                          </label>
                        </div>
                        <div className="col-xs-offset-2 col-xs-10 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-4 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="delivery_address_city"
                                name="delivery_address_city"
                                value={v('delivery_address_city')}
                                placeholder="City"
                                maxLength={30}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="delivery_address_city">
                                City
                              </label>
                            </div>
                            <div className="col-xs-2 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="delivery_address_st"
                                name="delivery_address_st"
                                value={v('delivery_address_st')}
                                placeholder="State"
                                maxLength={3}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="delivery_address_st">
                                State
                              </label>
                            </div>
                            <div className="col-xs-2 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="delivery_address_postal_code"
                                name="delivery_address_postal_code"
                                value={v('delivery_address_postal_code')}
                                placeholder="Postal Code"
                                maxLength={12}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="delivery_address_postal_code"
                              >
                                Postal Code
                              </label>
                            </div>
                            <div className="col-xs-4 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="delivery_address_country"
                                name="delivery_address_country"
                                value={v('delivery_address_country')}
                                placeholder="Country"
                                maxLength={15}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="delivery_address_country"
                              >
                                Country
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-7 col-md-12">
                    <div className="col-xs-12">
                      <div className="row">
                        <div className="label-text col-xs-2">Contact</div>
                        <div className="col-xs-10 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-6 input-with-placeholder-group ">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="delivery_co_contact_person"
                                name="delivery_co_contact_person"
                                value={v('delivery_co_contact_person')}
                                placeholder="Name"
                                maxLength={25}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="delivery_co_contact_person"
                              >
                                Name
                              </label>
                            </div>
                            <div className="col-xs-6 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder phone-number-input"
                                id="delivery_contact_tel_mobile"
                                name="delivery_contact_tel_mobile"
                                value={v('delivery_contact_tel_mobile')}
                                placeholder="Mobile"
                                maxLength={15}
                                pattern={PHONE_PATTERN}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="delivery_contact_tel_mobile"
                              >
                                Mobile
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-xs-offset-2 col-xs-10 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-6 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="delivery_contact_tel_office"
                                name="delivery_contact_tel_office"
                                value={v('delivery_contact_tel_office')}
                                placeholder="Office tel."
                                maxLength={15}
                                pattern={PHONE_PATTERN}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="delivery_contact_tel_office"
                              >
                                Office
                              </label>
                            </div>
                            <div className="col-xs-6 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder press_english"
                                type="email"
                                id="delivery_contact_email"
                                name="delivery_contact_email"
                                value={v('delivery_contact_email')}
                                placeholder="Email"
                                maxLength={35}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="delivery_contact_email">
                                Email
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-7 col-md-12 input-vertical-distance textarea-top-padding">
                    <div className="col-xs-12">
                      <div className="row">
                        <div className="label-text col-xs-2">Notes</div>
                        <div className="col-xs-10 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-12 input-with-placeholder-group ">
                              <OrderTextarea
                                className="long-input textarea"
                                clearClassName="text-clear notes-text-clear icon-close-1"
                                name="order_note"
                                value={v('order_note')}
                                maxLength={84}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        className={`icon-close-1 delete-order ka-has-tooltip${locked ? ' non-clickable' : ''}`}
        tooltip="Delete"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          if (locked) return;
          setShowDelete(true);
        }}
      />
      {showDelete && (
        <DeleteOrderPopup
          title="Delete Shipping Order?"
          content="If you choose to Delete, all Shipping Order information will be lost and will not be recoverable."
          name={v('order_number')}
          onConfirm={() => deleteOrder({ projectId, id: order.id })}
          onClose={() => setShowDelete(false)}
        />
      )}
    </li>
  );
};

export default ShippingOrderCard;
